// src/components/Avaliacao/AvaliacaoDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  Button,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { obterAvaliacao } from '../../services/avaliacaoService';

const AvaliacaoDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [avaliacao, setAvaliacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Base URL do backend (localhost vs produção)
  const baseUrl =
    window.location.hostname === 'localhost'
      ? 'http://localhost:5000'
      : 'https://api.zenkroll.co.mz';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await obterAvaliacao(id);
        console.log('Dados da avaliação:', response.data);
        setAvaliacao(response.data);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar avaliação:', err);
        setError('Não foi possível carregar os detalhes da avaliação.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ m: 4 }}>
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!avaliacao) {
    return (
      <Box sx={{ m: 4 }}>
        <Alert severity="info" variant="outlined">
          Avaliação não encontrada.
        </Alert>
      </Box>
    );
  }

  const handlePrintActa = () => {
    // 👉 Dados da instituição vindos diretamente do backend
    const nomeInstituicao = avaliacao?.instituicao?.nome || 'Nome da Instituição';
    const logoUrl = avaliacao?.instituicao?.logo
      ? `${baseUrl}/uploads/${avaliacao.instituicao.logo}`
      : `${baseUrl}/uploads/default-logo.png`;

    const dataDefesa = new Date(avaliacao.dataDefesa).toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    const formatNumber = (num) => Number(num || 0).toFixed(1);

    // Calcular os totais dos avaliadores dividindo por 2
    const totalPresidente = formatNumber(
      (Number(avaliacao.presidenteEscritoTotal) + Number(avaliacao.presidenteOralTotal)) / 2
    );
    const totalArguente = formatNumber(
      (Number(avaliacao.arguenteEscritoTotal) + Number(avaliacao.arguenteOralTotal)) / 2
    );
    const totalSupervisor = formatNumber(
      (Number(avaliacao.supervisorEscritoTotal) + Number(avaliacao.supervisorOralTotal)) / 2
    );

    // Formatar nomes (removendo títulos no início)
    const formatarNome = (nome) => {
      return (nome || '').replace(/^(Dr\.|PhD|Msc\.|Prof\.) /i, '').trim();
    };

    const actaHTML = `
      <html>
        <head>
          <title>Acta de Defesa</title>
          <style>
            @page { size: A4; margin: 2.5cm; }
            body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; text-align: justify; }
            .header { text-align: center; margin-bottom: 20px; }
            .header img { max-height: 90px; display:block; margin: 0 auto 8px; }
            .header h3 { margin: 5px 0; font-weight: bold; font-size: 12pt; }
            .main-title { font-weight: bold; margin: 15px 0; font-size: 12pt; }
            .jury-member { margin: 20px 0; position: relative; }
            .linha-assinatura { display: inline-block; width: 400px; border-bottom: 1px solid black; margin-right: 10px; }
            .nome-titulo { text-align: center; font-size: 11px; color: #444; margin-top: 5px; margin-left: 100px; }
            .score-line { display: flex; align-items: center; margin: 15px 0; }
            .score-label { min-width: 250px; }
            .score-dots { flex: 1; border-bottom: 1px solid black; margin: 0 10px; }
            .nota { display: inline-block; min-width: 50px; }
            .signature-section { text-align: center; margin-top: 30px; }
            .signature-line { width: 200px; border-bottom: 1px solid black; margin: 20px auto; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${logoUrl}" alt="Logotipo da Instituição" />
            <h3>${nomeInstituicao}</h3>
            <h3>DIRECÇÃO CIENTÍFICA</h3>
            <h3>UNIDADE DE INVESTIGAÇÃO CIENTIFICA E PUBLICAÇÕES</h3>
            <h3 class="main-title">ACTA DA DEFESA DE MONOGRAFIA CIENTÍFICA</h3>
          </div>

          <div style="margin-bottom: 20px;">
            Aos ${dataDefesa}, <strong>${avaliacao.nomeEstudante}</strong>, 
            candidato ao grau acadêmico de ${String(avaliacao.grauAcademico || '').toLowerCase()} em 
            <strong>${avaliacao.curso}</strong>, defendeu o trabalho intitulado 
            "<strong>${avaliacao.tema}</strong>"
          </div>

          <div style="margin: 15px 0;">
            <strong>O Júri da Defesa foi assim constituído:</strong>
          </div>
            
          <div class="jury-member">
            Presidente:<span class="linha-assinatura"></span>(${totalPresidente})
            <div class="nome-titulo">${formatarNome(avaliacao.presidenteNome)} (${avaliacao.presidenteTitulo || '-'})</div>
          </div>

          <div class="jury-member">
            Oponente:<span class="linha-assinatura"></span>(${totalArguente})
            <div class="nome-titulo">${formatarNome(avaliacao.arguenteNome)} (${avaliacao.arguenteTitulo || '-'})</div>
          </div>

          <div class="jury-member">
            Supervisor:<span class="linha-assinatura"></span>(${totalSupervisor})
            <div class="nome-titulo">${formatarNome(avaliacao.supervisorNome)} (${avaliacao.supervisorTitulo || '-'})</div>
          </div>

          <div style="margin: 20px 0;">
            <strong>Tendo sido atribuída a seguinte classificação:</strong>
          </div>

          <div class="score-line">
            <span class="score-label">Nota do Trabalho escrito</span>
            <span class="score-dots"></span>
            <span class="nota">(${formatNumber(avaliacao.notaTrabalhoEscrito)})</span> Valores
          </div>

          <div class="score-line">
            <span class="score-label">Nota da defesa de monografia Científica:</span>
            <span class="score-dots"></span>
            <span class="nota">(${formatNumber(avaliacao.notaDefesaOral)})</span> Valores
          </div>

          <div class="score-line">
            <span class="score-label">Média Final da Defesa:</span>
            <span class="score-dots"></span>
            <span class="nota">(${formatNumber(avaliacao.mediaFinal)})</span> Valores
          </div>

          <div class="signature-section">
            <p>O Director Científico e Pós Graduação</p>
            <div class="signature-line"></div>
            <p>${avaliacao.diretorCientifico || 'Director Científico'}</p>
          </div>
        </body>
      </html>
    `;

    const newWindow = window.open('', '_blank', 'width=900,height=600');
    newWindow.document.write(actaHTML);
    newWindow.document.close();
    newWindow.focus();
    setTimeout(() => {
      newWindow.print();
    }, 250);
  };

  // Cálculo dos totais
  const arguenteEscritoTotal = avaliacao.arguenteEscritoTotal || 0;
  const arguenteOralTotal = avaliacao.arguenteOralTotal || 0;
  const totalArguente = Number(arguenteEscritoTotal) + Number(arguenteOralTotal);

  const presidenteEscritoTotal = avaliacao.presidenteEscritoTotal || 0;
  const presidenteOralTotal = avaliacao.presidenteOralTotal || 0;
  const totalPresidente = Number(presidenteEscritoTotal) + Number(presidenteOralTotal);

  const supervisorEscritoTotal = avaliacao.supervisorEscritoTotal || 0;
  const supervisorOralTotal = avaliacao.supervisorOralTotal || 0;
  const totalSupervisor = Number(supervisorEscritoTotal) + Number(supervisorOralTotal);

  // Formatação para exibição com uma casa decimal
  const formatNumber = (num) => Number(num).toFixed(1);

  const renderTrabalhoEscrito = (prefixo) => (
    <List dense sx={{ mb: 2 }}>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Tema (2.0): <strong>{avaliacao[`${prefixo}EscritoTema`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Postura (2.0): <strong>{avaliacao[`${prefixo}EscritoPostura`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Introdução (2.0): <strong>{avaliacao[`${prefixo}EscritoIntroducao`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Revisão (3.0): <strong>{avaliacao[`${prefixo}EscritoRevisao`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Metodologia (3.0): <strong>{avaliacao[`${prefixo}EscritoMetodologia`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Argumentos (5.0): <strong>{avaliacao[`${prefixo}EscritoArgumentos`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Conclusões (3.0): <strong>{avaliacao[`${prefixo}EscritoConclusoes`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
    </List>
  );

  const renderDefesaOral = (prefixo) => (
    <List dense sx={{ mb: 1 }}>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Domínio do Tema (4.0): <strong>{avaliacao[`${prefixo}OralDominioTema`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Clareza / Organização (3.0): <strong>{avaliacao[`${prefixo}OralClarezaOrganizacao`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Argumentação / Debate (4.0): <strong>{avaliacao[`${prefixo}OralArgumentacaoDebate`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Postura (2.0): <strong>{avaliacao[`${prefixo}OralPostura`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Respostas ao Júri (3.0): <strong>{avaliacao[`${prefixo}OralRespostasJuri`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Capacidade de Síntese (2.0): <strong>{avaliacao[`${prefixo}OralCapacidadeSintese`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
      <ListItem>
        <ListItemText
          primary={
            <Typography variant="body2">
              Observância do Tempo (2.0): <strong>{avaliacao[`${prefixo}OralObservanciaTempo`] ?? '--'}</strong>
            </Typography>
          }
        />
      </ListItem>
    </List>
  );

  const renderAvaliador = (titulo, prefixo, subtotalEscrito, subtotalOral, total) => {
    // Calcular o total dividido por 2
    const totalDividido = formatNumber((Number(subtotalEscrito) + Number(subtotalOral)) / 2);

    return (
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            mb: 2,
            color: 'primary.main',
            borderBottom: '2px solid #2196F3',
            paddingBottom: 1
          }}
        >
          {titulo}
        </Typography>

        {/* TRABALHO ESCRITO */}
        <Typography
          variant="subtitle1"
          sx={{
            color: 'secondary.main',
            mb: 1,
            backgroundColor: '#f5f5f5',
            padding: '8px',
            borderRadius: '4px',
            fontWeight: 'bold'
          }}
        >
          Trabalho Escrito
        </Typography>
        {renderTrabalhoEscrito(prefixo)}

        <Typography
          variant="body1"
          sx={{
            mb: 2,
            fontWeight: 'bold',
            textAlign: 'right',
            backgroundColor: '#e3f2fd',
            padding: '8px',
            borderRadius: '4px'
          }}
        >
          Subtotal (Escrito): <strong>{formatNumber(subtotalEscrito)}</strong>
        </Typography>

        {/* DEFESA ORAL */}
        <Typography
          variant="subtitle1"
          sx={{
            color: 'secondary.main',
            mb: 1,
            backgroundColor: '#f5f5f5',
            padding: '8px',
            borderRadius: '4px',
            fontWeight: 'bold'
          }}
        >
          Defesa Oral
        </Typography>
        {renderDefesaOral(prefixo)}

        <Typography
          variant="body1"
          sx={{
            mb: 2,
            fontWeight: 'bold',
            textAlign: 'right',
            backgroundColor: '#e3f2fd',
            padding: '8px',
            borderRadius: '4px'
          }}
        >
          Subtotal (Oral): <strong>{formatNumber(subtotalOral)}</strong>
        </Typography>

        {/* TOTAL (agora dividido por 2) */}
        <Typography
          variant="h6"
          sx={{
            textAlign: 'right',
            color: 'primary.main',
            borderTop: '2px solid #2196F3',
            paddingTop: 1,
            marginTop: 2
          }}
        >
          Total: <strong>{totalDividido}</strong>
        </Typography>
      </Paper>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', my: 4, px: 2 }}>
      <Card sx={{ borderRadius: 3, boxShadow: 6 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <AssessmentIcon fontSize="large" />
            </Avatar>
          }
          title={
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'white' }}>
              {avaliacao.tema}
            </Typography>
          }
          subheader={
            <Typography variant="subtitle1" color="white">
              Estudante: {avaliacao.nomeEstudante}
            </Typography>
          }
          sx={{
            background: 'linear-gradient(45deg, #2196F3, #21CBF3)',
            color: 'white',
            p: 3
          }}
        />
        <CardContent>
          {/* Informações Gerais da Defesa */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Curso
                </Typography>
                <Typography variant="body1">{avaliacao.curso}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Grau Acadêmico
                </Typography>
                <Typography variant="body1">{avaliacao.grauAcademico}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Data da Defesa
                </Typography>
                <Typography variant="body1">
                  {new Date(avaliacao.dataDefesa).toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Destaque para a Média Final */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                display: 'inline-block',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196F3, #21CBF3)',
                color: '#fff',
                px: 4,
                py: 2,
                borderRadius: 3,
                boxShadow: 3
              }}
            >
              Média Final: {formatNumber(avaliacao.mediaFinal)}
            </Typography>
          </Box>

          {/* Notas do Trabalho Escrito e Defesa Oral */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <Typography variant="h6">
                  Nota Trabalho Escrito: {formatNumber(avaliacao.notaTrabalhoEscrito)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <Typography variant="h6">
                  Nota Defesa Oral: {formatNumber(avaliacao.notaDefesaOral)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Três colunas: Arguente, Presidente, Supervisor */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              {renderAvaliador(
                'Arguente',
                'arguente',
                arguenteEscritoTotal,
                arguenteOralTotal,
                totalArguente
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderAvaliador(
                'Presidente',
                'presidente',
                presidenteEscritoTotal,
                presidenteOralTotal,
                totalPresidente
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderAvaliador(
                'Supervisor',
                'supervisor',
                supervisorEscritoTotal,
                supervisorOralTotal,
                totalSupervisor
              )}
            </Grid>
          </Grid>

          {/* Rodapé */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 4,
              pt: 2,
              borderTop: '1px solid rgba(0, 0, 0, 0.12)'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Última atualização: {new Date(avaliacao.updatedAt).toLocaleString()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" color="secondary" onClick={handlePrintActa}>
                Imprimir Acta
              </Button>
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
              >
                Voltar
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AvaliacaoDetails;
